/**
 * 팝업 관리 페이지 - 멀티 슬라이드 관리
 * popups 컬렉션에 여러 팝업을 등록/삭제/순서/활성 관리
 */
import React, { useCallback, useState, useEffect } from 'react';
import {
  Box, Button, Chip, Paper, Typography, Alert, TextField,
  Switch, IconButton, Tooltip, CircularProgress, Snackbar,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Campaign as CampaignIcon,
  Link as LinkIcon,
  Add as AddIcon,
  ArrowUpward,
  ArrowDownward,
  Image as ImageIcon,
} from '@mui/icons-material';
import { db, auth } from '../../firebase';
import { collection, onSnapshot, query, orderBy, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import PromoPopup from '../components/PromoPopup';
import type { PopupDoc } from '../hooks/useActivePopup';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'https://api-4iuywcitfq-uc.a.run.app';

export default function PopupAdminPage() {
  const [popups, setPopups] = useState<PopupDoc[]>([]);
  const [loading, setLoading] = useState(true);

  // 등록 폼
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [title, setTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [saving, setSaving] = useState(false);

  // 미리보기
  const [previewOpen, setPreviewOpen] = useState(false);

  // 스낵바
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // 실시간 구독
  useEffect(() => {
    const q = query(collection(db, 'popups'), orderBy('order', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setLoading(false);
      const docs: PopupDoc[] = snap.docs.map((d) => ({ id: d.id, ...d.data() } as PopupDoc));
      setPopups(docs);
    });
    return () => unsub();
  }, []);

  // 파일 선택
  const handleFile = useCallback((f: File) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result || ''));
    reader.readAsDataURL(f);
  }, []);

  // 새 팝업 등록 - 기존 백엔드 API로 Storage 업로드 후 URL을 popups 컬렉션에 저장
  const handleAdd = async () => {
    if (!file) return;
    try {
      setSaving(true);

      // 1. 인증 토큰 획득
      const user = auth.currentUser;
      if (!user) { alert('관리자 로그인이 필요합니다.'); return; }
      const token = await user.getIdToken(true);

      // 2. 백엔드 API로 이미지 업로드 (Storage 저장은 서버가 처리)
      const base64 = await fileToBase64(file);
      const res = await fetch(`${API_BASE_URL}/admin/popups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ title: title || '', linkUrl: linkUrl || '', base64Image: base64 }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result?.error || '업로드 실패');

      // 3. 반환된 downloadUrl을 popups 컬렉션에 저장
      const nextOrder = popups.length > 0 ? Math.max(...popups.map(p => p.order)) + 1 : 1;
      await addDoc(collection(db, 'popups'), {
        downloadUrl: result.downloadUrl,
        title: title || '',
        linkUrl: linkUrl || '',
        linkTarget: '_blank',
        isActive: true,
        order: nextOrder,
        startAt: null,
        endAt: null,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });

      setSnackbar({ open: true, message: '팝업이 등록되었습니다.', severity: 'success' });
      setFile(null);
      setPreview('');
      setTitle('');
      setLinkUrl('');
    } catch (e: any) {
      setSnackbar({ open: true, message: '등록 실패: ' + (e?.message || ''), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 파일을 Base64로 변환
  const fileToBase64 = (f: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });

  // 삭제
  const handleDelete = async (id: string) => {
    if (!confirm('이 팝업을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'popups', id));
      setSnackbar({ open: true, message: '삭제되었습니다.', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: '삭제 실패', severity: 'error' });
    }
  };

  // 활성/비활성 토글
  const handleToggle = async (popup: PopupDoc) => {
    try {
      await updateDoc(doc(db, 'popups', popup.id), {
        isActive: !popup.isActive,
        updatedAt: Timestamp.now(),
      });
    } catch {
      setSnackbar({ open: true, message: '상태 변경 실패', severity: 'error' });
    }
  };

  // 순서 변경
  const handleMoveOrder = async (popup: PopupDoc, direction: 'up' | 'down') => {
    const idx = popups.findIndex(p => p.id === popup.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= popups.length) return;
    const other = popups[swapIdx];
    try {
      await Promise.all([
        updateDoc(doc(db, 'popups', popup.id), { order: other.order }),
        updateDoc(doc(db, 'popups', other.id), { order: popup.order }),
      ]);
    } catch {
      setSnackbar({ open: true, message: '순서 변경 실패', severity: 'error' });
    }
  };

  const activePopups = popups.filter(p => p.isActive);

  return (
    <Box sx={{ pb: 4 }}>
      {/* 헤더 */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', borderRadius: 3, color: '#fff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CampaignIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>팝업 관리</Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>프로모션 팝업을 등록하고 슬라이드 순서를 관리합니다</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label={`전체 ${popups.length}개`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }} />
            <Chip label={`활성 ${activePopups.length}개`} sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: '#fff', fontWeight: 700 }} />
            {activePopups.length > 0 && (
              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => setPreviewOpen(true)}
                sx={{ bgcolor: '#fff', color: '#6366f1', fontWeight: 700, '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
              >
                미리보기
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* 팝업 목록 */}
      <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
          <Typography fontWeight={700} sx={{ fontSize: '0.95rem' }}>등록된 팝업 ({popups.length})</Typography>
        </Box>

        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : popups.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <ImageIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ color: '#94a3b8' }}>등록된 팝업이 없습니다</Typography>
          </Box>
        ) : (
          <Box>
            {popups.map((popup, idx) => (
              <Box
                key={popup.id}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  px: 3, py: 2,
                  borderBottom: idx < popups.length - 1 ? '1px solid #f0f0f0' : 'none',
                  '&:hover': { bgcolor: '#fafbfc' },
                  transition: 'background 0.15s',
                }}
              >
                {/* 순서 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3 }}>
                  <IconButton size="small" disabled={idx === 0} onClick={() => handleMoveOrder(popup, 'up')} sx={{ width: 24, height: 24 }}>
                    <ArrowUpward sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Chip label={idx + 1} size="small" sx={{ minWidth: 28, bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: '0.7rem' }} />
                  <IconButton size="small" disabled={idx === popups.length - 1} onClick={() => handleMoveOrder(popup, 'down')} sx={{ width: 24, height: 24 }}>
                    <ArrowDownward sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                {/* 썸네일 */}
                <Box
                  component="img"
                  src={popup.downloadUrl}
                  alt={popup.title || '팝업'}
                  sx={{
                    width: 100, height: 60,
                    objectFit: 'cover',
                    borderRadius: 1.5,
                    border: '1px solid #e5e7eb',
                    flexShrink: 0,
                  }}
                />

                {/* 정보 */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {popup.title || '(제목 없음)'}
                  </Typography>
                  {popup.linkUrl && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                      <LinkIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {popup.linkUrl}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* 상태 + 액션 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Tooltip title={popup.isActive ? '비활성화' : '활성화'}>
                    <Switch
                      checked={popup.isActive}
                      onChange={() => handleToggle(popup)}
                      size="small"
                      color="success"
                    />
                  </Tooltip>
                  <Tooltip title="삭제">
                    <IconButton size="small" onClick={() => handleDelete(popup.id)} sx={{ color: '#ef4444' }}>
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* 새 팝업 등록 */}
      <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 3, py: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
          <Typography fontWeight={700} sx={{ fontSize: '0.95rem' }}>새 팝업 등록</Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* 이미지 업로드 */}
          <Box
            sx={{
              border: '2px dashed #d1d5db',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 2.5,
              bgcolor: '#fafbfc',
              transition: 'all 0.2s',
              '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.02)' },
            }}
            onClick={() => document.getElementById('popup-file-input')?.click()}
          >
            {preview ? (
              <Box component="img" src={preview} alt="미리보기" sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1.5, objectFit: 'contain' }} />
            ) : (
              <>
                <CloudUploadIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
                <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>클릭하여 이미지 선택</Typography>
                <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.5 }}>PNG, JPG, WebP (최대 5MB)</Typography>
              </>
            )}
            <input
              id="popup-file-input"
              hidden type="file" accept="image/*"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </Box>

          {/* 제목 + 링크 */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
            <TextField
              fullWidth size="small"
              label="제목 (선택)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 신규 이벤트 안내"
            />
            <TextField
              fullWidth size="small"
              label="링크 URL (선택)"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
            />
          </Box>

          {/* 등록 버튼 */}
          <Button
            variant="contained" fullWidth
            onClick={handleAdd}
            disabled={!file || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            sx={{
              py: 1.3, borderRadius: 2, fontWeight: 700,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
            }}
          >
            {saving ? '등록 중...' : '팝업 등록'}
          </Button>
        </Box>
      </Paper>

      {/* 도움말 */}
      <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
        <Typography variant="body2">
          <strong>팁:</strong> 활성화된 팝업들이 순서대로 캐러셀 슬라이드로 표시됩니다. 순서 화살표로 표시 순서를 변경하세요.
        </Typography>
      </Alert>

      {/* 미리보기 모달 */}
      <PromoPopup
        forceOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        isPreviewMode
        previewPopups={activePopups}
      />

      {/* 스낵바 */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
